// Order-submission flow for the garment Designer, extracted from Designer.tsx.
// Owns the submit-modal state and the two async actions (openSubmit composites
// per-location proofs + uploads them; placeOrder POSTs the order). The behaviour
// is a verbatim move — all of the Designer's canvas refs, helpers and state are
// passed in via `deps` so the logic is unchanged.
import { useState } from 'react'
import type { RefObject, MutableRefObject, Dispatch, SetStateAction } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { fabric } from 'fabric'
import { apiFetch } from '../../lib/api'
import { CANVAS_W, CANVAS_H, canvasHasMedia, type Tool } from './helpers'
import type { CatProduct } from '../../lib/catalog-types'

export interface SubmitState {
  open: boolean
  proof?: string
  proofs?: Array<{ locationId: string; locationName: string; dataUrl: string }>
  proofFileId?: string
  confirmed: boolean
  busy: boolean
}

interface FileResp { id: string; url: string }
interface DesignRow { id: string }

export interface ShippingForm {
  fullName: string; email: string; phone: string; line1: string; line2: string
  city: string; region: string; postcode: string; country: string
}

type GuideJSON = { objects?: Array<{ type?: string; strokeDashArray?: unknown; text?: string }> }

export interface UseDesignerSubmitDeps {
  product: CatProduct | null | undefined
  totals: { totalQty: number }
  colourId: string | null
  qty: Record<string, number>
  shipping: ShippingForm
  designId: string | null
  setDesignId: Dispatch<SetStateAction<string | null>>
  setDirty: Dispatch<SetStateAction<boolean>>
  setTool: Dispatch<SetStateAction<Tool>>
  setNeedsSizes: Dispatch<SetStateAction<boolean>>
  sizesRef: RefObject<HTMLDivElement | null>
  fabricRef: RefObject<fabric.Canvas | null>
  activeLocationIdRef: MutableRefObject<string | null>
  locationStatesRef: MutableRefObject<Record<string, object>>
  printGuideRef: MutableRefObject<fabric.Rect | null>
  printLabelRef: MutableRefObject<fabric.Text | null>
  pieceThumbnailsRef: MutableRefObject<Record<string, string>>
  stripGuidesFromJSON: (json: GuideJSON) => GuideJSON
  purgeStrayGuides: () => void
  persist: () => Promise<void>
  buildState: () => object | null
  apiSrc: (path?: string | null) => string
  getAccessToken: unknown
  navigate: NavigateFunction
}

export function useDesignerSubmit(deps: UseDesignerSubmitDeps) {
  const {
    product, totals, colourId, qty, shipping, designId,
    setDesignId, setDirty, setTool, setNeedsSizes,
    sizesRef, fabricRef, activeLocationIdRef, locationStatesRef,
    printGuideRef, printLabelRef, pieceThumbnailsRef,
    stripGuidesFromJSON, purgeStrayGuides, persist, buildState, apiSrc,
    getAccessToken, navigate,
  } = deps

  const [submit, setSubmit] = useState<SubmitState>({ open: false, confirmed: false, busy: false })

  async function openSubmit() {
    if (totals.totalQty === 0) {
      // Soft redirect: switch the right-hand panel to the Sizes tab,
      // flash a highlighted nudge above the inputs, and scroll the panel
      // into view. Beats the old blunt alert() that just blocked the
      // customer without telling them where to fix the problem.
      setTool('product')
      setNeedsSizes(true)
      window.setTimeout(() => sizesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50)
      return
    }
    const c = fabricRef.current!
    // Snapshot the live canvas + active location id BEFORE we start so we
    // can restore everything no matter how the loop below exits.
    const startingLocationId = activeLocationIdRef.current
    if (startingLocationId) {
      locationStatesRef.current[startingLocationId] = stripGuidesFromJSON(c.toJSON() as GuideJSON)
    }
    printGuideRef.current!.visible = false; printLabelRef.current!.visible = false
    const restoreBg = (c as fabric.Canvas).backgroundImage as fabric.Image | string | undefined
    const setBgAsync = (url: string | null) => new Promise<void>((resolve) => {
      if (!url) { c.setBackgroundImage(null as unknown as fabric.Image, () => { c.requestRenderAll(); resolve() }); return }
      fabric.Image.fromURL(apiSrc(url), (img) => {
        if (!img) { resolve(); return }
        const sx = CANVAS_W / (img.width ?? CANVAS_W)
        const sy = CANVAS_H / (img.height ?? CANVAS_H)
        const s = Math.min(sx, sy)
        img.set({ scaleX: s, scaleY: s, originX: 'left', originY: 'top',
                  left: (CANVAS_W - (img.width ?? CANVAS_W) * s) / 2,
                  top:  (CANVAS_H - (img.height ?? CANVAS_H) * s) / 2 })
        c.setBackgroundImage(img, () => { c.requestRenderAll(); resolve() })
      }, { crossOrigin: 'anonymous' })
    })
    const loadCanvasAsync = (json: object) => new Promise<void>((resolve) => {
      c.loadFromJSON(json, () => {
        purgeStrayGuides()
        c.requestRenderAll(); resolve()
      })
    })

    // Walk every location with media, composite each one against its own
    // mockup (falling back to the product-level mockup if a location doesn't
    // override it), and capture a preview PNG.
    const locs = product?.printLocations ?? []
    const proofs: Array<{ locationId: string; locationName: string; dataUrl: string }> = []
    for (const loc of locs) {
      const stored = locationStatesRef.current[loc.id]
      if (!canvasHasMedia(stored as { objects?: Array<{ type?: string; text?: string; strokeDashArray?: unknown }> })) continue
      await loadCanvasAsync(stored)
      await setBgAsync(loc.mockupUrl ?? product?.mockupUrl ?? null)
      proofs.push({ locationId: loc.id, locationName: loc.name, dataUrl: c.toDataURL({ format: 'png', multiplier: 2 }) })
    }

    // Restore the active location's canvas + background + guides.
    if (startingLocationId && locationStatesRef.current[startingLocationId]) {
      await loadCanvasAsync(locationStatesRef.current[startingLocationId])
    }
    c.setBackgroundImage(restoreBg ?? null as unknown as fabric.Image, () => {
      printGuideRef.current!.visible = true; printLabelRef.current!.visible = true
      c.requestRenderAll()
    })

    // Primary proof = the active location's preview if it has one, otherwise
    // the first proof. Kept for legacy proofFileId + the order-detail page's
    // single-proof slot.
    const primary = proofs.find((p) => p.locationId === startingLocationId) ?? proofs[0]
    setSubmit({ open: true, proof: primary?.dataUrl, proofs, confirmed: false, busy: false })

    // Upload the primary proof in the background so the place-order call has
    // a fileId ready when the customer confirms.
    if (primary) {
      try {
        const up = await apiFetch<FileResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
          method: 'POST',
          body: JSON.stringify({ originalName: `proof-${primary.locationName.toLowerCase().replace(/\s+/g, '-')}.png`, mimeType: 'image/png', base64: primary.dataUrl, kind: 'proof' }),
        })
        setSubmit((s) => ({ ...s, proofFileId: up.id }))
      } catch { /* user can still submit without proofFileId */ }
    }

    // ALSO upload each per-location preview as a flat thumbnail (kind=
    // 'mockup') so the admin pages can <img> the same baked PNG that the
    // customer is about to submit. Keyed by location id; the API copies
    // these into artwork_approvals.thumb_file_id when seeding pieces.
    const thumbMap: Record<string, string> = {}
    await Promise.all(proofs.map(async (p) => {
      try {
        const up = await apiFetch<FileResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
          method: 'POST',
          body: JSON.stringify({
            originalName: `mockup-${p.locationName.toLowerCase().replace(/\s+/g, '-')}.png`,
            mimeType: 'image/png',
            base64: p.dataUrl,
            kind: 'mockup',
          }),
        })
        thumbMap[`location:${p.locationId}`] = up.id
      } catch { /* one thumb failing shouldn't block submit */ }
    }))
    pieceThumbnailsRef.current = thumbMap
    // Persist (PATCH or POST) so the design's spec carries the
    // pieceThumbnails map by the time placeOrder reads designId.
    await persist()
  }

  async function placeOrder() {
    if (!product) return
    setSubmit((s) => ({ ...s, busy: true }))
    try {
      // Auto-save was removed, so designId can still be null at submit time
      // if the user never pressed Save. Persist now so the order has a
      // valid designId to attach — otherwise /api/orders rejects with 400
      // and the user just sees a dead button.
      let did = designId
      if (!did) {
        const spec = buildState()
        if (!spec) throw new Error('Could not capture design')
        const row = await apiFetch<DesignRow>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/designs', {
          method: 'POST', body: JSON.stringify({ kind: 'garment', spec }),
        })
        did = row.id
        setDesignId(row.id); setDirty(false)
      }
      const res = await apiFetch<{ id: string }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/orders', {
        method: 'POST',
        body: JSON.stringify({
          designId: did, productId: product.id, colourId, sizes: qty,
          proofFileId: submit.proofFileId ?? null, shipping,
        }),
      })
      navigate(`/orders/${res.id}`)
    } catch (e) {
      alert(`Could not place order: ${(e as Error).message}`)
      setSubmit((s) => ({ ...s, busy: false }))
    }
  }

  return { submit, setSubmit, openSubmit, placeOrder }
}
